/**
 * OSR leads — the page's data: the summary (tab counts, my numbers, new
 * leads, my claim cooldown) and the current view's list.
 *
 * Fresh by push, safe by poll. The server announces every change on the
 * live connection ("osr:changed"); the page refetches when it hears one.
 * One slow poll runs underneath as the safety net for a dropped connection.
 *
 *   POLL BUDGET  /summary every 60 s and the visible list every 60 s, only
 *   while this page is open and its browser tab is visible (usePoll pauses
 *   hidden tabs and never overlaps). Two requests a minute per open page.
 *
 * A refresh that fails never clears what is on screen. The last good data
 * stays, `stale` turns true, and the page says so in one line.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import usePoll from "../../hooks/usePoll";
import { useSocketEvent } from "../../hooks/useSocketEvent";
import { osrFetch } from "./osrShared";
import { filterQuery } from "./useOsrFilters";

export const LEAD_CHANGED = "osr:changed";
const POLL_MS = 60000;
const PAGE_SIZE = 25;
const LIST_VIEWS = ["working", "followups", "closed", "spam"];
// Typing and notes announce often and move little on the board (a name, the
// Today check); they wait longer and ride along with anything quicker.
export const QUIET_ACTIONS = new Set(["edited", "note", "enriched"]);
const QUIET_MS = 2500;
const QUICK_MS = 350;

/** A notice for this person's own typing or note. */
export function ownQuietChange(msg, userId) {
  return !!(msg && userId && msg.by === userId && QUIET_ACTIONS.has(msg.action));
}

export default function useOsrBoard({ filters, page, userId }) {
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState({ view: null, leads: [], total: 0, page: 1, page_size: PAGE_SIZE });
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState(null); // only when there is nothing to show
  const [stale, setStale] = useState(false);

  // The latest request wins: a slow answer for an old filter must not
  // replace a newer one.
  const listSeq = useRef(0);
  const hasSummary = useRef(false);
  const shownView = useRef(null);
  const view = filters.view;
  const query = filterQuery(filters).toString();
  const isList = LIST_VIEWS.includes(view);

  const loadSummary = useCallback(async () => {
    try {
      const json = await osrFetch("/summary");
      hasSummary.current = true;
      setSummary(json);
      setStale(false);
      setError(null);
      return true;
    } catch (err) {
      setStale(true);
      if (!hasSummary.current) setError(err);
      return false; // tells usePoll to back off
    }
  }, []);

  const loadList = useCallback(async ({ quiet = false } = {}) => {
    if (!isList) return true;
    const seq = ++listSeq.current;
    if (!quiet) setListLoading(true);
    try {
      const q = new URLSearchParams(query);
      q.set("view", view);
      q.set("page", String(page));
      q.set("page_size", String(PAGE_SIZE));
      const json = await osrFetch(`/leads?${q.toString()}`);
      if (seq !== listSeq.current) return true;
      shownView.current = view;
      setList(json);
      setStale(false);
      setError(null);
      return true;
    } catch (err) {
      if (seq !== listSeq.current) return true;
      setStale(true);
      if (shownView.current !== view) setError(err);
      return false;
    } finally {
      if (seq === listSeq.current) setListLoading(false);
    }
  }, [isList, view, query, page]);

  useEffect(() => { loadList(); }, [loadList]);

  usePoll(loadSummary, POLL_MS);
  usePoll(() => loadList({ quiet: true }), POLL_MS, { immediate: false, enabled: isList });

  // A burst of changes is one refetch. This person's own typing and notes
  // are skipped: the request that made them already refreshed the board
  // (a second tab of theirs catches up on the poll).
  const timer = useRef(null);
  const due = useRef(0);
  const refresh = useCallback((msg) => {
    if (ownQuietChange(msg, userId)) return;
    const wait = msg && QUIET_ACTIONS.has(msg.action) ? QUIET_MS : QUICK_MS;
    const at = Date.now() + wait;
    if (timer.current && due.current <= at) return; // something sooner is already scheduled
    if (timer.current) clearTimeout(timer.current);
    due.current = at;
    timer.current = setTimeout(() => {
      timer.current = null;
      loadSummary();
      loadList({ quiet: true });
    }, wait);
  }, [loadSummary, loadList, userId]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useSocketEvent(LEAD_CHANGED, refresh);

  const refreshNow = useCallback(() => Promise.all([loadSummary(), loadList({ quiet: true })]), [loadSummary, loadList]);

  return {
    summary,
    list: list.view === view ? list : { view, leads: [], total: 0, page: 1, page_size: PAGE_SIZE },
    listLoading: listLoading || (isList && list.view !== view),
    error,
    stale,
    refresh: refreshNow,
    pageSize: PAGE_SIZE,
  };
}
